import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Briefcase,
  Receipt,
  Eye,
  Edit,
  MoreHorizontal,
  Filter,
  ChevronDown,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import { invoiceValidationService } from '../../services/invoiceValidationService';

// Services
import billingService from '../../services/billingService';
import { contractFinanceService } from '../../services/contractFinanceService';
import ContractService from '../../services/contractService';

const BillingDashboard = () => {
  const { success, error: showError, toasts, removeToast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('projects');
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]); // Store original response
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateFormData, setGenerateFormData] = useState({
    invoiceNumber: '',
    amount: '',
    currency: 'USD',
    invoiceDate: new Date().toISOString().split('T')[0],
    status: 'pending', // Added default status
  });

  // Find selected option for the modal text
  const selectedOption = options.find((opt) => opt.id === selectedId);

  // --- 1. Load Dropdown Options ---
  useEffect(() => {
    const fetchOptions = async () => {
      setSelectedId('');
      setOptions([]);
      setRawData([]);

      try {
        if (activeTab === 'projects') {
          const endpoint = `${API_CONFIG.ENDPOINTS.PROJECTS}?limit=1000`;
          const response = await fetch(endpoint, {
            headers: API_CONFIG.HEADERS,
          });

          if (!response.ok) throw new Error('Failed to fetch projects');

          const data = await response.json();
          const projectsList =
            data.data?.projects || data.projects || data.data || [];

          setOptions(
            projectsList.map((p) => ({
              id: p._id || p.id,
              name: p.name || p.projectName || 'Unnamed Project',
            })),
          );
        } else {
          const response = await ContractService.getContracts();
          const list = response.data || response || [];
          setOptions(
            list.map((c) => ({
              id: c._id || c.id,
              name: c.contractName || c.title || `Contract ${c.contractNumber}`,
            })),
          );
        }
      } catch (err) {
        console.error('Option Error:', err);
        showError(`Could not load ${activeTab} list.`);
      }
    };

    fetchOptions();
  }, [activeTab]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      let response;
      if (activeTab === 'projects') {
        response = await billingService.getProjectInvoices(
          selectedId ? { projectId: selectedId } : {},
        );
      } else {
        // CONTRACTS LOAD LOGIC
        if (selectedId) {
          response =
            await contractFinanceService.getContractInvoices(selectedId);
        } else {
          // If no selection, your backend might not support "" - try calling analytics
          // but specifically look for the data array within it.
          response = await contractFinanceService.getAnalytics();
        }
      }

      console.log(`[DEBUG] ${activeTab} Response:`, response);

      let extractedData = [];
      if (response) {
        // Based on your log: { success: true, data: [...] }
        if (Array.isArray(response.data)) {
          extractedData = response.data;
        } else if (Array.isArray(response)) {
          extractedData = response;
        } else if (response.invoices && Array.isArray(response.invoices)) {
          extractedData = response.invoices;
        }
      }

      setRawData(extractedData);
    } catch (err) {
      console.error('Load Error:', err);
      if (selectedId) showError('Failed to load billing details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [selectedId, activeTab]);

  // --- 3. Robust Filter Logic ---
  const filteredInvoices = useMemo(() => {
    if (!Array.isArray(rawData)) return [];
    if (!selectedId) return rawData;

    return rawData.filter((item) => {
      if (!item) return false;

      // Projects: Check 'project', 'projectId', or 'company'
      const pId =
        item.project?._id || item.project || item.projectId || item.company;

      // Contracts: Check 'contractId' or 'contract'
      const cId = item.contractId || item.contract?._id || item.contract;

      const targetId = activeTab === 'projects' ? pId : cId;

      return String(targetId) === String(selectedId);
    });
  }, [rawData, selectedId, activeTab]);

  // Add this inside BillingDashboard or as a separate helper
  const ActionDropdown = ({ invoice, onRefresh }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const { success, error: showError } = useToast();

    const handleView = () => {
      setIsOpen(false);
      const id = invoice?.id || invoice?._id || invoice?.invoiceId;
      if (id) {
        navigate(`/projects/time-tracking/billing/view/${id}`, {
          state: { invoice },
        });
        return;
      }
      success('Viewing invoice details');
    };

    const handleEdit = () => {
      setIsOpen(false);
      const id = invoice?.id || invoice?._id || invoice?.invoiceId;
      if (id) {
        navigate(`/projects/time-tracking/billing/edit/${id}`, {
          state: { invoice },
        });
        return;
      }
      success('Opening invoice editor');
    };

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };

      const handleScroll = () => setIsOpen(false);

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        // 'true' catches the scroll event in the capturing phase (useful for tables)
        window.addEventListener('scroll', handleScroll, true);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [isOpen]);

    const handleToggle = () => {
      if (!isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        // If there's less than 150px space below, open upward
        setOpenUpward(window.innerHeight - rect.bottom < 150);
      }
      setIsOpen((prev) => !prev);
    };

    const handleSendForValidation = async () => {
      setIsOpen(false);
      try {
        // Mapping Billing Row -> InvoiceValidation Schema
        const payload = {
          // Required Strings
          invoiceNumber:
            invoice.invoiceNumber || invoice.reference || `INV-${Date.now()}`,
          vendorName:
            invoice.counterparty || invoice.vendorName || 'Unknown Vendor',
          currency: invoice.currency || 'USD',

          // Required Date (must be a valid Date object or ISO string)
          invoiceDate:
            invoice.date || invoice.dueDate || new Date().toISOString(),

          // Required Number (Schema uses 'set: roundCurrency')
          amount: Number(invoice.amount) || 0,

          // Enum Fields (Defaults to 'Rule-Based' if not provided)
          validationMethod: 'Rule-Based',

          // Processing Queue Priority (Schema expects Number 1-5, default 3)
          priority: invoice.amount > 10000 ? 1 : 3,
        };

        console.log('Sending to Backend:', payload);

        const response =
          await invoiceValidationService.validateInvoice(payload);

        success(`Invoice ${payload.invoiceNumber} sent for validation`);

        if (onRefresh) onRefresh();
      } catch (error) {
        // If the error is a 400, this will show the schema validation error
        showError(error.message || 'Validation submission failed');
        console.error('Schema Mismatch Details:', error);
      }
    };

    return (
      <div
        ref={dropdownRef}
        className="relative flex justify-center"
        style={{ zIndex: isOpen ? 10000 : 'auto' }}
      >
        {/* Action Button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggle}
          className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-gray-500 rounded hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500 transition-all shadow-sm"
        >
          <MoreHorizontal size={16} />
        </button>

        {/* Dropdown - Uses Fixed positioning to break out of table containers */}
        {isOpen && (
          <div
            className="fixed min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-xl"
            style={{
              zIndex: 10001,
              [openUpward ? 'bottom' : 'top']: openUpward
                ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
                : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
              left: `${buttonRef.current.getBoundingClientRect().left}px`,
              transform: 'translateX(-85%)', // Adjusted to align better with the button
            }}
          >
            <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-50 bg-gray-50/50 rounded-t-lg">
              Invoice Management
            </div>

            <ul className="py-1 m-0 list-none">
              <li>
                <button
                  onClick={handleView}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <Eye size={16} className="mr-3 text-gray-400" />
                  View Details
                </button>
              </li>

              <li>
                <button
                  onClick={handleSendForValidation}
                  className="flex items-center w-full px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 transition-colors text-left font-medium"
                >
                  <RefreshCw size={16} className="mr-3 text-amber-500" />
                  Send for Validation
                </button>
              </li>

              <li className="border-t border-gray-100 my-1"></li>

              <li>
                <button
                  onClick={handleEdit}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <Edit size={16} className="mr-3 text-gray-400" />
                  Edit Invoice
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    );
  };
  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'name',
      label: activeTab === 'projects' ? 'Project Name' : 'Contract Title',
      render: (_, row) => {
        // Logic specific to your console log: 'contractTitle'
        const name =
          activeTab === 'projects'
            ? row.project?.name || row.projectName || row.name
            : row.contractTitle || row.contract?.title || row.title || '—';

        return (
          <span className="font-medium text-gray-900 text-sm">{name}</span>
        );
      },
    },
    {
      key: 'status',
      label: 'Payment Status',
      render: (status) => {
        const s = status?.toLowerCase() || 'pending';
        const isPaid = s === 'paid';
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
              isPaid
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {s}
          </span>
        );
      },
    },
    {
      key: 'date',
      label: 'Date',
      render: (_, row) => {
        // Check for both fields; Contracts usually use dueDate, Projects use date
        const dateVal = row.date || row.dueDate || row.createdAt;
        return (
          <span className="text-sm">
            {dateVal ? new Date(dateVal).toLocaleDateString() : '—'}
          </span>
        );
      },
    },
    {
      key: 'approvalStatus',
      label: 'Approval',
      render: (status) => (
        <span
          className={`text-sm font-medium ${status === 'Approved' ? 'text-blue-600' : 'text-gray-500'}`}
        >
          {status || 'Under Review'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (_, row) => <ActionDropdown invoice={row} />,
    },
  ];

  const handleGenerateClick = () => {
    if (!selectedId) {
      showError('Please select a project or contract first.');
      return;
    }
    setGenerateFormData({
      ...generateFormData,
      invoiceNumber: `INV-${Date.now().toString(36).toUpperCase()}`,
      amount: '',
      invoiceDate: new Date().toISOString().split('T')[0],
    });
    setShowGenerateModal(true);
  };

  // Function called when the Modal Form is submitted
  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const statusValue = generateFormData.status || 'pending';

      if (activeTab === 'projects') {
        const projectPayload = {
          invoiceNumber: generateFormData.invoiceNumber,
          amount: Number(generateFormData.amount),
          currency: generateFormData.currency,
          date: generateFormData.invoiceDate,
          status: statusValue.toLowerCase(), // Projects expect lowercase
          project: selectedId,
          projectName: selectedOption?.name || '',
          vendorName: selectedOption?.name || '',
          clientName: 'Internal Project',
        };

        await billingService.createProjectInvoice(projectPayload);
      } else {
        // CONTRACT LOGIC: Map Dashboard Form -> Payment Schema
        // Create a contract payment record (payments endpoint does not require a milestone)
        const paymentPayload = {
          amount: Number(generateFormData.amount),
          dueDate: generateFormData.invoiceDate,
          invoiceNumber: generateFormData.invoiceNumber,
          // Important: Use capitalized status for contract payments
          status:
            generateFormData.status.charAt(0).toUpperCase() +
            generateFormData.status.slice(1),
          paymentType: 'One-Time',
          paymentMethod: 'Bank Transfer',
          notes: `Manual billing record generated for ${selectedOption?.name}`,
        };

        await contractFinanceService.createContractPayment(
          selectedId,
          paymentPayload,
        );
      }

      success(
        `${activeTab === 'projects' ? 'Invoice' : 'Payment'} generated successfully`,
      );
      setShowGenerateModal(false);

      // Refresh the table
      setTimeout(() => loadInvoices(), 1000);
    } catch (err) {
      const serverMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message;
      showError(`Generation Error: ${serverMessage}`);
      console.error('Full Error Details:', err);
    } finally {
      setLoading(false);
    }
  };

  const headerActions = [
    {
      icon: <RefreshCw size={16} className={loading ? 'spinning' : ''} />,
      variant: 'outline',
      onClick: loadInvoices,
    },
    {
      label: `Generate ${activeTab === 'projects' ? 'Invoice' : 'Billing'}`,
      icon: <Plus size={18} />,
      onClick: handleGenerateClick, // Changed this
      variant: 'primary',
      disabled: !selectedId || loading,
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Financial Billing"
        description="Select a record to manage invoices and payments"
        actions={headerActions}
      />

      <div className="page-container">
        {/* Tab Switcher */}
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <button
            onClick={() => setActiveTab('projects')}
            className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-2 transition-colors ${activeTab === 'projects' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <Briefcase size={14} /> Project Billing
          </button>
          <button
            onClick={() => setActiveTab('contracts')}
            className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-2 transition-colors ${activeTab === 'contracts' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <Receipt size={14} /> Contract Billing
          </button>
        </div>

        {/* Selection Dropdown - Adjusted Font/Size */}
        <div className="bg-gray-50 border border-gray-200 p-3 rounded-xl mb-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 block">
              {activeTab === 'projects'
                ? 'Project Selection'
                : 'Contract Selection'}
            </label>
            <div className="relative">
              <select
                className="w-full pl-3 pr-10 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-1 focus:ring-blue-500 outline-none text-sm text-gray-700 appearance-none"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">{`Select a ${activeTab.slice(0, -1)}...`}</option>
                {options.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>
          {!selectedId && (
            <div className="flex items-center gap-2 text-amber-600 text-[13px] font-medium bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100">
              <Filter size={14} /> Select to load records
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <DataTable
            data={filteredInvoices}
            columns={columns}
            loading={loading}
            showActions={false}
            emptyMessage={
              selectedId
                ? 'No billing records found for this selection.'
                : 'Please select an item above to view billing data.'
            }
          />
        </div>
      </div>
      {showGenerateModal && (
        <div
          className="fixed inset-0 flex items-center justify-center p-5 z-[1050]"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={(e) =>
            e.target === e.currentTarget && setShowGenerateModal(false)
          }
        >
          <div className="bg-white rounded-2xl p-7 w-full max-w-[500px] shadow-2xl">
            <h3 className="text-lg font-bold mb-1">
              Generate New Billing Record
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              For:{' '}
              <span className="text-blue-600 font-semibold">
                {selectedOption?.name}
              </span>
            </p>

            <form onSubmit={handleGenerateSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">
                  Invoice Reference
                </label>
                <input
                  type="text"
                  value={generateFormData.invoiceNumber}
                  onChange={(e) =>
                    setGenerateFormData({
                      ...generateFormData,
                      invoiceNumber: e.target.value,
                    })
                  }
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={generateFormData.amount}
                    onChange={(e) =>
                      setGenerateFormData({
                        ...generateFormData,
                        amount: e.target.value,
                      })
                    }
                    required
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">
                    Currency
                  </label>
                  <select
                    value={generateFormData.currency}
                    onChange={(e) =>
                      setGenerateFormData({
                        ...generateFormData,
                        currency: e.target.value,
                      })
                    }
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">
                  Issue Date
                </label>
                <input
                  type="date"
                  value={generateFormData.invoiceDate}
                  onChange={(e) =>
                    setGenerateFormData({
                      ...generateFormData,
                      invoiceDate: e.target.value,
                    })
                  }
                  required
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">
                  Status
                </label>
                <select
                  value={generateFormData.status}
                  onChange={(e) =>
                    setGenerateFormData({
                      ...generateFormData,
                      status: e.target.value,
                    })
                  }
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Generate & Queue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default BillingDashboard;
